import { Component, Host, h, Prop, Element, Event, EventEmitter } from '@stencil/core';
import { getAriaLabel, getParentForm, renderHiddenInput, addEventListener, removeEventListener } from '../../utils/helpers';
import { HorizontalIconListItemChangeEventDetail } from './vds-horizontal-icon-list-item.interface';

@Component({
  tag: 'vds-horizontal-icon-list-item',
  styleUrl: 'vds-horizontal-icon-list-item.scss',
  shadow: true,
})
export class VdsHorizontalIconListItem {

  private inputId = `vds-horizontal-icon-list-item-${listItemIds++}`;
  private focusEl?: HTMLElement;
  private form: HTMLFormElement | null = null;

  @Element() el!: HTMLVdsHorizontalIconListItemElement;

  /**
   * Text underneath the icon
   */
  @Prop() label: string;

  /**
   * If true the item has a border
   */
  @Prop() border: boolean = false;

  /**
   * Hover effect
   */
  @Prop() hover: boolean = false;

  /**
   * URL to open on click
   */
  @Prop() href?: string = undefined;

  /**
   * Disables the interaction
   */
  @Prop() disabled: boolean = false;

  /**
   * Sets the interaction mode
   * determines wether or not the component can be clicked
   */
  @Prop() mode: 'default' | 'link' | 'form-control' = 'default';

  /**
   * The name of the control, which is submitted with the form data.
   */
  @Prop() name: string = this.inputId;

  /**
   * Hidden value of the checkbox
   */
  @Prop() value: any | null = 'on';

  /**
   * If `true`, the item is selected.
   */
  @Prop({ mutable: true }) selected = false;

  /**
   * Emitted when the checked property has changed.
   */
  @Event() vdsChange!: EventEmitter<HorizontalIconListItemChangeEventDetail>;

  /**
   * Emitted when the checkbox has focus.
   */
  @Event() vdsFocus!: EventEmitter<void>;

  /**
   * Emitted when the checkbox loses focus.
   */
  @Event() vdsBlur!: EventEmitter<void>;

  render() {
    const { selected, disabled, border, hover, el, inputId, name, value, label } = this;
    const { labelId, labelText } = getAriaLabel(el, inputId, label);

    renderHiddenInput(true, el, name, (selected ? value : ''), disabled);

    return (
      <Host
        aria-labelledby={label ? labelId : null}
        aria-checked={`${selected}`}
        aria-hidden={disabled ? 'true' : null}
        role="checkbox">
        <div
          id="itemContainer"
          class={{
            "relative grid h-full text-grey bg-white": true,
            "opacity-25 pointer-events-none": disabled,
            "border-2": border,
            "hover:shadow-[0_14px_25px_rgba(0,0,0,.16)]": hover,
            "border-berry": selected,
            "border-grey-lighter": !selected
          }}>
          { this.renderItemContent(labelId, labelText) }
        </div>
      </Host>
    );
  }

  private renderItemContent(labelId, labelText): string {
    if(this.mode == 'link') {
      return (
        <a href={ this.href } class="h-full">
          <div class="text-center py-[15px] px-[10px]">
            <div class="block mx-auto">
              <slot name="icon"></slot>
            </div>
            <p class="text-sm mt-[30px]">{ this.label }</p>
          </div>
        </a>
      );
    }
    else if(this.mode == 'form-control') {
      const { selected, disabled, inputId } = this;

      return (
        <label id={labelId} htmlFor={inputId} class="h-full cursor-pointer">
          <input
          onClick={this.handleClick}
          type="checkbox"
          class="hidden"
          aria-checked={`${selected}`}
          disabled={disabled}
          id={inputId}
          onFocus={() => this.onFocus()}
          onBlur={() => this.onBlur()}
          ref={focusEl => (this.focusEl = focusEl)}
          checked={selected}/>
          <div class="text-center py-[15px] px-[10px]">
            <div
              class={{
                "block mx-auto": true,
                "text-berry": selected
              }}>
              <slot name="icon"></slot>
            </div>
            <p
              class={{
              "text-sm mt-[30px]": true,
              "text-berry": selected
              }}>
              { labelText }
            </p>
          </div>
        </label>
      );
    }
    else if(this.mode == 'default') {
      return (
        <span class="h-full">
          <div class="text-center py-[15px] px-[10px]">
            <div class="block mx-auto">
              <slot name="icon"></slot>
            </div>
            <p class="text-sm mt-[30px]">{ this.label }</p>
          </div>
        </span>
      );
    }
  }

  connectedCallback() {
    if(this.mode == 'form-control') {
      const form = this.form = getParentForm(this.el);
      if (form) {
        addEventListener(form, 'reset', this.onReset);
      }
    }
  }

  disconnectedCallback() {
    if(this.mode == 'form-control') {
      const form = this.form;
      if (form) {
        removeEventListener(form, 'reset', this.onReset)
      }
    }
  }

  private handleClick = (ev: any) => {
    ev.preventDefault();
    this.setFocus();
    this.selected = !this.selected;
  }

  private setFocus() {
    if (this.focusEl) {
      this.focusEl.focus();
    }
  }

  private onFocus = () => {
    this.vdsFocus.emit();
  };

  private onBlur = () => {
    this.vdsBlur.emit();
  };

  private onReset = () => {
    this.selected = false;
  }

}

let listItemIds = 0;
